import { useMsal } from "@azure/msal-react";
import { useMutation } from "@tanstack/react-query";
import { callPymbleApi } from "./usePymbleApi";

// S2 endpoint — group notifications
const API_END_POINT = "group/admin/notify/";

export interface GroupNotification {
	Method: "push" | "email";
	Title: string;
	Body: string;
	Groups: string[];
	From?: string;
}

/**
 * Send a push or email notification to whole groups.
 *
 * Ported from event-manager-v2's hook of the same name. Note the API notifies
 * GROUPS, not individual contacts — there is no per-attendee send, which is
 * why the attendee row checkboxes drive the export rather than this.
 */
export function useGroupNotify() {
	const { instance } = useMsal();

	const mutation = useMutation({
		mutationFn: async (notification: GroupNotification) => {
			// S2 uses "push" (S1 used "send")
			const requestType = notification.Method === "push" ? "push" : "email";
			const request: Record<string, unknown> = {
				Title: notification.Title,
				Body: notification.Body,
				Groups: notification.Groups,
			};
			if (requestType === "email" && notification.From === "ce") {
				request["From"] = "ce";
			}
			return callPymbleApi(
				instance,
				"POST",
				`${API_END_POINT}?request_type=${requestType}`,
				{ data: request }
			);
		},
	});

	const sendToGroups = async (notification: GroupNotification) => {
		// No payload echoed — the notification targets groups of real people.
		return mutation.mutateAsync(notification);
	};

	return {
		sendToGroups,
		notificationSending: mutation.isPending,
	};
}
